import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Leaf, MapPin, Calendar, Clock, ChevronLeft, Loader2, Navigation,
  Plane, Music, Volume2, VolumeX, Thermometer, Wind, Snowflake, MessageSquare, Save
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { useAuth } from '../context/AuthContext';
import { GooglePlacesAutocomplete } from '../components/GoogleMaps';
import { toast } from 'sonner';
import axios from 'axios';
import { format, addHours, setHours, setMinutes, isBefore, addDays } from 'date-fns';
import { fr } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ModifyBookingPage = () => {
  const navigate = useNavigate();
  const { bookingId } = useParams();
  const { getAuthHeader } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [booking, setBooking] = useState(null);
  
  // Form state
  const [pickupAddress, setPickupAddress] = useState('');
  const [pickupCoords, setPickupCoords] = useState(null);
  const [destAddress, setDestAddress] = useState('');
  const [destCoords, setDestCoords] = useState(null);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState('');
  const [flightNumber, setFlightNumber] = useState('');
  const [ambiance, setAmbiance] = useState('calme');
  const [climate, setClimate] = useState('auto');
  const [specialRequests, setSpecialRequests] = useState('');

  useEffect(() => {
    const fetchBooking = async () => {
      try {
        const response = await axios.get(`${API}/bookings/${bookingId}`, {
          headers: getAuthHeader()
        });
        const b = response.data;
        setBooking(b);
        
        // Populate form
        setPickupAddress(b.pickup_address || '');
        setPickupCoords(b.pickup_lat && b.pickup_lng ? { lat: b.pickup_lat, lng: b.pickup_lng } : null);
        setDestAddress(b.destination_address || '');
        setDestCoords(b.destination_lat && b.destination_lng ? { lat: b.destination_lat, lng: b.destination_lng } : null);
        
        const pickupDt = new Date(b.pickup_datetime);
        setDate(pickupDt);
        setTime(format(pickupDt, 'HH:mm'));
        
        setFlightNumber(b.flight_number || '');
        setAmbiance(b.ambiance || 'calme');
        setClimate(b.climate || 'auto');
        setSpecialRequests(b.special_requests || '');
      } catch (error) {
        toast.error('Réservation non trouvée');
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };
    
    fetchBooking();
  }, [bookingId, getAuthHeader, navigate]);

  const generateTimeSlots = () => {
    const slots = [];
    for (let h = 0; h < 24; h++) {
      for (let m = 0; m < 60; m += 5) {
        const hour = h.toString().padStart(2, '0');
        const minute = m.toString().padStart(2, '0');
        slots.push(`${hour}:${minute}`);
      }
    }
    return slots;
  };

  const handleSubmit = async () => {
    if (!date || !time) {
      toast.error('Veuillez sélectionner une date et une heure');
      return;
    }
    
    const [hours, minutes] = time.split(':').map(Number);
    const pickupDateTime = setMinutes(setHours(date, hours), minutes);
    const minDateTime = addHours(new Date(), 5);
    
    if (isBefore(pickupDateTime, minDateTime)) {
      toast.error('La réservation doit être au minimum 5h à l\'avance');
      return;
    }
    
    setSaving(true);
    try {
      const updateData = {
        pickup_datetime: pickupDateTime.toISOString(),
        flight_number: flightNumber || null,
        ambiance,
        climate,
        special_requests: specialRequests || null
      };
      
      // Include address changes if modified
      if (pickupAddress !== booking.pickup_address && pickupCoords) {
        updateData.pickup_address = pickupAddress;
        updateData.pickup_lat = pickupCoords.lat;
        updateData.pickup_lng = pickupCoords.lng;
      }
      
      if (destAddress !== booking.destination_address && destCoords) {
        updateData.destination_address = destAddress;
        updateData.destination_lat = destCoords.lat;
        updateData.destination_lng = destCoords.lng;
      }
      
      await axios.put(
        `${API}/bookings/${bookingId}/modify`,
        updateData,
        { headers: getAuthHeader() }
      );
      
      toast.success('Réservation modifiée avec succès !');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la modification');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090b]">
      {/* Header */}
      <header className="px-6 py-6 border-b border-zinc-800">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <button 
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-zinc-400 hover:text-white"
          >
            <ChevronLeft className="w-5 h-5" />
            Retour
          </button>
          <div className="flex items-center gap-2">
            <img src="/logo-youmi.png" alt="YOUMI GREEN VTC" className="w-12 h-12" />
            <span className="font-bold text-lg font-['Outfit'] text-white">YOUMI GREEN</span>
            <span className="text-emerald-500 font-bold text-lg font-['Outfit']">VTC</span>
          </div>
        </div>
      </header>

      <main className="px-6 py-12">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl font-bold text-white font-['Outfit'] mb-8">
            Modifier ma réservation
          </h1>

          <div className="glass-card rounded-2xl p-6 space-y-6">
            {/* Date & Time */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-zinc-300 flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-emerald-500" />
                  Date de prise en charge
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700"
                    >
                      {date ? format(date, 'EEEE d MMMM yyyy', { locale: fr }) : 'Sélectionner une date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-zinc-900 border-zinc-700" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      disabled={(d) => isBefore(d, addDays(new Date(), -1))}
                      locale={fr}
                      className="rounded-md"
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label className="text-zinc-300 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-emerald-500" />
                  Heure de prise en charge
                </Label>
                <select
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                  className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                >
                  <option value="">Sélectionner</option>
                  {generateTimeSlots().map(slot => (
                    <option key={slot} value={slot}>{slot}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Addresses */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-zinc-300 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-emerald-500" />
                  Adresse de prise en charge
                </Label>
                <GooglePlacesAutocomplete
                  placeholder="Modifier l'adresse de départ"
                  value={pickupAddress}
                  icon={MapPin}
                  onSelect={(result) => {
                    setPickupAddress(result.display_name);
                    setPickupCoords({ lat: result.lat, lng: result.lng });
                  }}
                />
              </div>

              <div className="space-y-2">
                <Label className="text-zinc-300 flex items-center gap-2">
                  <Navigation className="w-4 h-4 text-red-500" />
                  Destination
                </Label>
                <GooglePlacesAutocomplete
                  placeholder="Modifier la destination"
                  value={destAddress}
                  icon={Navigation}
                  onSelect={(result) => {
                    setDestAddress(result.display_name);
                    setDestCoords({ lat: result.lat, lng: result.lng });
                  }}
                />
              </div>
            </div>

            {/* Flight Number */}
            <div className="space-y-2">
              <Label className="text-zinc-300 flex items-center gap-2">
                <Plane className="w-4 h-4 text-emerald-500" />
                Numéro de vol (optionnel)
              </Label>
              <Input
                value={flightNumber}
                onChange={(e) => setFlightNumber(e.target.value)}
                placeholder="Ex: AF1234"
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>

            {/* Ambiance */}
            <div className="space-y-2">
              <Label className="text-zinc-300 flex items-center gap-2">
                <Music className="w-4 h-4 text-emerald-500" />
                Ambiance souhaitée
              </Label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: 'calme', label: 'Calme', icon: VolumeX },
                  { value: 'musique', label: 'Musique', icon: Music },
                  { value: 'conversation', label: 'Discussion', icon: MessageSquare }
                ].map(({ value, label, icon: Icon }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setAmbiance(value)}
                    className={`flex flex-col items-center gap-2 p-3 rounded-lg border transition-colors ${
                      ambiance === value
                        ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400'
                        : 'border-zinc-700 text-zinc-400 hover:border-zinc-600'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="text-sm">{label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Climate */}
            <div className="space-y-2">
              <Label className="text-zinc-300 flex items-center gap-2">
                <Thermometer className="w-4 h-4 text-emerald-500" />
                Climatisation / Chauffage
              </Label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: 'auto', label: 'Auto', icon: Wind },
                  { value: 'climatisation', label: 'Clim', icon: Wind },
                  { value: 'chauffage', label: 'Chauffage', icon: Thermometer }
                ].map(({ value, label, icon: Icon }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setClimate(value)}
                    className={`flex flex-col items-center gap-2 p-3 rounded-lg border transition-colors ${
                      climate === value
                        ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400'
                        : 'border-zinc-700 text-zinc-400 hover:border-zinc-600'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="text-sm">{label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Special Requests */}
            <div className="space-y-2">
              <Label className="text-zinc-300">Demandes spéciales (optionnel)</Label>
              <textarea
                value={specialRequests}
                onChange={(e) => setSpecialRequests(e.target.value)}
                placeholder="Siège bébé, bagages volumineux, assistance mobilité..."
                className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder:text-zinc-500 resize-none"
                rows={3}
              />
            </div>

            {/* Submit Button */}
            <div className="flex gap-4 pt-4">
              <Button
                variant="outline"
                className="flex-1 border-zinc-700"
                onClick={() => navigate('/dashboard')}
              >
                Annuler
              </Button>
              <Button
                className="flex-1 btn-primary"
                onClick={handleSubmit}
                disabled={saving}
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Enregistrer les modifications
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};
